import React, { useEffect, useRef } from "react";
import "./SpaceBackground.css";

// Subtle star field rendered on canvas
const StarCanvas = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const starsRef = useRef([]);
  const galaxyStarsRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let width = canvas.width = canvas.offsetWidth;
    let height = canvas.height = canvas.offsetHeight;

    // Create background stars (sparse)
    const createStars = () => {
      const stars = [];
      const starCount = Math.floor((width * height) / 12000);

      for (let i = 0; i < starCount; i++) {
        stars.push({
          x: Math.random() * width,
          y: Math.random() * height,
          size: Math.random() * 1.2 + 0.3,
          baseOpacity: Math.random() * 0.4 + 0.2,
          twinkleSpeed: Math.random() * 0.0008 + 0.0003,
          twinkleOffset: Math.random() * Math.PI * 2,
          twinkleChance: Math.random(),
        });
      }
      return stars;
    };

    // Galaxy center point
    const galaxyCenterX = width / 2;
    const galaxyCenterY = height / 2;

    // Create galaxy band stars (concentrated along diagonal) with orbital data
    const createGalaxyStars = () => {
      const stars = [];
      const starCount = Math.floor((width * height) / 4000); // More stars in galaxy

      for (let i = 0; i < starCount; i++) {
        // Position along diagonal from bottom-left to top-right
        const t = Math.random();
        const centerX = t * width;
        const centerY = height - t * height;

        // Spread from the diagonal line (gaussian-like distribution)
        const spread = (Math.random() + Math.random() + Math.random()) / 3 - 0.5;
        const bandWidth = Math.min(width, height) * 0.25;

        const x = centerX + spread * bandWidth;
        const y = centerY + spread * bandWidth;

        // Calculate polar coordinates from galaxy center for orbital motion
        const dx = x - galaxyCenterX;
        const dy = y - galaxyCenterY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const angle = Math.atan2(dy, dx);

        // Only add if within bounds
        if (x >= 0 && x <= width && y >= 0 && y <= height) {
          stars.push({
            distance,
            angle,
            size: Math.random() * 0.8 + 0.2,
            baseOpacity: Math.random() * 0.5 + 0.3,
            twinkleSpeed: Math.random() * 0.001 + 0.0003,
            twinkleOffset: Math.random() * Math.PI * 2,
            twinkleChance: Math.random(),
            // Orbital speed - closer stars orbit faster (Kepler's law approximation)
            orbitSpeed: 0.00002 + (0.00003 / (distance / 100 + 1)),
          });
        }
      }
      return stars;
    };

    starsRef.current = createStars();
    galaxyStarsRef.current = createGalaxyStars();

    // Shooting star state
    let shootingStar = null;
    let lastShootingStarTime = 0;
    const shootingStarInterval = 8000 + Math.random() * 12000; // 8-20 seconds

    const createShootingStar = () => {
      // Angle: top-left to bottom-right direction with variation (roughly 30° to 60°)
      const angle = Math.PI / 6 + Math.random() * Math.PI / 6; // 30° to 60°

      // Start from top-left area
      const startX = Math.random() * width * 0.4; // Left 40% of screen
      const startY = Math.random() * height * 0.3; // Top 30% of screen

      return {
        x: startX,
        y: startY,
        speed: 2 + Math.random() * 2,
        length: 80 + Math.random() * 60,
        opacity: 1,
        angle: angle,
      };
    };

    const animate = (time) => {
      ctx.clearRect(0, 0, width, height);

      // Draw background stars (static)
      starsRef.current.forEach((star) => {
        let opacity = star.baseOpacity;

        // Only 30% of stars twinkle, and very slowly
        if (star.twinkleChance > 0.7) {
          const twinkle = Math.sin(time * star.twinkleSpeed + star.twinkleOffset);
          opacity = star.baseOpacity * (0.7 + twinkle * 0.3);
        }

        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
        ctx.fill();
      });

      // Draw galaxy stars (orbiting)
      galaxyStarsRef.current.forEach((star) => {
        // Update angle for orbital motion
        star.angle += star.orbitSpeed;

        // Convert polar to cartesian coordinates
        const x = galaxyCenterX + Math.cos(star.angle) * star.distance;
        const y = galaxyCenterY + Math.sin(star.angle) * star.distance;

        let opacity = star.baseOpacity;

        // Only 30% of stars twinkle
        if (star.twinkleChance > 0.7) {
          const twinkle = Math.sin(time * star.twinkleSpeed + star.twinkleOffset);
          opacity = star.baseOpacity * (0.7 + twinkle * 0.3);
        }

        ctx.beginPath();
        ctx.arc(x, y, star.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
        ctx.fill();
      });

      // Shooting star logic
      if (!shootingStar && time - lastShootingStarTime > shootingStarInterval) {
        shootingStar = createShootingStar();
        lastShootingStarTime = time;
      }

      if (shootingStar) {
        // Move shooting star
        shootingStar.x += Math.cos(shootingStar.angle) * shootingStar.speed;
        shootingStar.y += Math.sin(shootingStar.angle) * shootingStar.speed;

        // Draw shooting star trail
        const gradient = ctx.createLinearGradient(
          shootingStar.x - Math.cos(shootingStar.angle) * shootingStar.length,
          shootingStar.y - Math.sin(shootingStar.angle) * shootingStar.length,
          shootingStar.x,
          shootingStar.y
        );
        gradient.addColorStop(0, 'rgba(255, 255, 255, 0)');
        gradient.addColorStop(0.7, `rgba(167, 139, 250, ${shootingStar.opacity * 0.5})`);
        gradient.addColorStop(1, `rgba(255, 255, 255, ${shootingStar.opacity})`);

        ctx.beginPath();
        ctx.moveTo(
          shootingStar.x - Math.cos(shootingStar.angle) * shootingStar.length,
          shootingStar.y - Math.sin(shootingStar.angle) * shootingStar.length
        );
        ctx.lineTo(shootingStar.x, shootingStar.y);
        ctx.strokeStyle = gradient;
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // Draw bright head
        ctx.beginPath();
        ctx.arc(shootingStar.x, shootingStar.y, 1.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${shootingStar.opacity})`;
        ctx.fill();

        // Reset when off screen
        if (shootingStar.x > width + 50 || shootingStar.y > height + 50) {
          shootingStar = null;
        }
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate(0);

    const handleResize = () => {
      width = canvas.width = canvas.offsetWidth;
      height = canvas.height = canvas.offsetHeight;
      starsRef.current = createStars();
      galaxyStarsRef.current = createGalaxyStars();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
    />
  );
};

// Main Space Background Component - Subtle version
const SpaceBackground = () => {
  return (
    <div className="space-background-subtle">
      {/* Deep space gradient base */}
      <div className="space-gradient-subtle" />

      {/* Subtle galaxy band - bottom left to top right */}
      <div className="galaxy-band" />

      {/* Subtle animated glow */}
      <div className="ambient-glow glow-1" />
      <div className="ambient-glow glow-2" />

      {/* Star field canvas */}
      <StarCanvas />

      {/* Soft vignette */}
      <div className="vignette-subtle" />
    </div>
  );
};

export default SpaceBackground;
